// src/components/StateBoundaries.tsx
import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
interface HeatMapDataPoint {
  warning_type: string;
  city: string;
  state: string | null;
  lat: string;
  long: string;
  count: number;
}

interface StateBoundariesProps {
  visible: boolean;
  heatMapData?: HeatMapDataPoint[];
  selectedAlertType?: string | null;
  country?: 'India' | 'Nigeria' | 'Zambia' | 'Saudi Arabia';
}

const StateBoundaries: React.FC<StateBoundariesProps> = ({
  visible,
  heatMapData = [],
  selectedAlertType = null,
  country = 'India',
}: StateBoundariesProps) => {
  const map = useMap();
  const [geojsonData, setGeojsonData] = useState<any>(null);

  // Map country to GeoJSON filename
  const getGeoJsonFile = (countryName: string) => {
    const countryMap: { [key: string]: string } = {
      India: 'india-states.geojson',
      Nigeria: 'nigeria-states.geojson',
      Zambia: 'zambia-states.geojson',
      'Saudi Arabia': 'saudi-arabia-states.geojson',
    };
    return countryMap[countryName] || 'india-states.geojson';
  };

  // Load GeoJSON data
  useEffect(() => {
    // Use PUBLIC_URL (when set) or a relative path so this works when app is served from a subpath
    const base = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '' ? process.env.PUBLIC_URL : '';
    const geoJsonFile = getGeoJsonFile(country);
    const geojsonUrl = base ? `${base}/${geoJsonFile}` : `/${geoJsonFile}`;

    fetch(geojsonUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch ${geojsonUrl}: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => setGeojsonData(data))
      .catch((error) => console.error('Error loading GeoJSON:', error));
  }, [country]);

  useEffect(() => {
    if (!visible || !geojsonData) return;

    // Filter data by selected alert type first and exclude null states
    let filteredData = heatMapData.filter((d) => d.state !== null && d.state?.trim());
    if (selectedAlertType && selectedAlertType !== '') {
      filteredData = filteredData.filter((d) => d.warning_type === selectedAlertType);
    }

    // Calculate state intensity from filtered data
    const stateCountMap = new Map<string, number>();

    filteredData.forEach((item) => {
      if (item.state && item.state.trim()) {
        const currentCount = stateCountMap.get(item.state) || 0;
        stateCountMap.set(item.state, currentCount + item.count);
      }
    });

    // Get states with data for filtering
    const statesWithData = new Set(stateCountMap.keys());

    // Get max count for normalization
    const maxCount = Math.max(...Array.from(stateCountMap.values()), 1);

    // Function to get color based on intensity
    const getColor = (intensity: number) => {
      if (intensity > 0.8) return '#FF0000';
      if (intensity > 0.6) return '#FF9800';
      if (intensity > 0.4) return '#FF9800';
      if (intensity > 0.2) return '#F2C94C';
      return '#235AF9';
    };

    // Function to get intensity for a state from actual data
    const getIntensity = (stateName: string): number | null => {
      // Try exact match first
      if (stateCountMap.has(stateName)) {
        const count = stateCountMap.get(stateName) || 0;
        return count / maxCount;
      }

      // Try partial match
      for (const [key, count] of stateCountMap.entries()) {
        if (stateName.includes(key) || key.includes(stateName)) {
          return count / maxCount;
        }
      }

      return null; // No data for this state
    };

    // Function to get actual count for a state
    const getCount = (stateName: string): number | null => {
      // Try exact match first
      if (stateCountMap.has(stateName)) {
        return stateCountMap.get(stateName) || 0;
      }

      // Try partial match
      for (const [key, count] of stateCountMap.entries()) {
        if (stateName.includes(key) || key.includes(stateName)) {
          return count;
        }
      }

      return null; // No data for this state
    };

    // Create GeoJSON layer with real boundaries
    const geojsonLayer = L.geoJSON(geojsonData, {
      style: (feature: any) => {
        const stateName = feature?.properties?.NAME_1 || feature?.properties?.name || '';
        const intensity = getIntensity(stateName);

        // If no data for this state, show clear boundaries without fill
        if (intensity === null) {
          return {
            fillColor: 'transparent',
            weight: 2,
            opacity: 1,
            color: 'white',
            dashArray: '3',
            fillOpacity: 0,
          };
        }

        return {
          fillColor: getColor(intensity),
          weight: 2,
          opacity: 0.9,
          color: 'white',
          dashArray: '3',
          fillOpacity: 0.6,
        };
      },
      onEachFeature: (feature: any, layer: L.Layer) => {
        const stateName = feature?.properties?.NAME_1 || feature?.properties?.name || 'Unknown';
        const intensity = getIntensity(stateName);
        const count = getCount(stateName);

        // Only show popups for states with data
        if (intensity !== null && count !== null) {
          // Bind popup with count instead of intensity
          layer.bindPopup(`
            <div style="padding: 10px; min-width: 200px;">
              <strong style="font-size: 16px; color: #333333;">${stateName}</strong><br/>
              <div style="margin-top: 8px; font-size: 14px;">
                📊 Total Count: <strong>${count.toLocaleString()}</strong>
              </div>
              <div style="margin-top: 10px;">
                <div style="width: 100%; height: 8px; background: #E0E0E0; border-radius: 4px;">
                  <div style="width: ${intensity * 100}%; height: 100%; background: ${getColor(
            intensity,
          )}; border-radius: 4px;"></div>
                </div>
              </div>
            </div>
          `);
        }

        const pathLayer = layer as L.Path;

        // Add hover effects only for states with data
        layer.on('mouseover', function () {
          if (intensity !== null) {
            pathLayer.setStyle({
              weight: 3,
              fillOpacity: 0.7,
            });
          }
        });

        layer.on('mouseout', function () {
          if (intensity !== null) {
            pathLayer.setStyle({
              weight: 2,
              fillOpacity: 0.5,
            });
          }
        });
      },
    }).addTo(map);

    // Set proper z-index for boundaries to appear above heatmap
    (geojsonLayer as any).setZIndex(100);

    // Cleanup
    return () => {
      if (map.hasLayer(geojsonLayer)) {
        map.removeLayer(geojsonLayer);
      }
    };
  }, [map, visible, geojsonData, heatMapData, selectedAlertType]);

  return null;
};

export default StateBoundaries;
